import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";

/**
 * MagneticButton — a real <button> that gently moves toward the cursor on
 * hover, then springs back when the pointer leaves. Forwards data-testid and
 * className so it's a drop-in replacement.
 */
const MagneticButton = ({
  children,
  onClick,
  className = "",
  style = {},
  strength = 18,
  ...rest
}) => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 220, damping: 20, mass: 0.4 });
  const sy = useSpring(my, { stiffness: 220, damping: 20, mass: 0.4 });
  const x = useTransform(sx, (v) => v);
  const y = useTransform(sy, (v) => v);

  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const cx = r.left + r.width / 2;
    const cy = r.top + r.height / 2;
    const dx = (e.clientX - cx) / r.width;
    const dy = (e.clientY - cy) / r.height;
    mx.set(dx * strength);
    my.set(dy * strength);
  };
  const onLeave = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <motion.button
      ref={ref}
      onClick={onClick}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      style={{ ...style, x, y }}
      className={className}
      whileTap={{ scale: 0.97 }}
      transition={{ type: "spring", stiffness: 400, damping: 22 }}
      {...rest}
    >
      {children}
    </motion.button>
  );
};

export default MagneticButton;
